import { useState } from 'react';
import { B } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Textarea, Select, Modal, Card, Table, Tabs, KpiCard, Badge, InfoGrid, Spinner, ErrorMsg, SectionHeader, useToast, Icon } from '../components/ui';
import { ParcelsMap } from '../components/shared/ParcelsMapTab';
import { ParcelsList } from '../components/shared/ParcelsList';
import { FieldDetectionModal } from '../components/shared/FieldDetectionModal';
import { FieldBoundaryEditor } from '../components/shared/FieldBoundaryEditor';
import { polygonAcres, polygonCentroid } from '../utils/fieldGeo';
import { FARM_TYPES, FARM_STATUS, num, weatherDesc, windCompass } from '../constants/farm';

// ---------------------------------------------------------------------------
// Shared option lists & helpers
// ---------------------------------------------------------------------------
const LAND_USE        = ['', 'Crop', 'Pasture', 'Hay', 'Fallow', 'Orchard', 'Other'];
const IRRIGATION      = ['', 'Dryland', 'Center Pivot', 'Drip', 'Flood', 'Sprinkler', 'Other'];
const FIELD_STATUS    = ['Active', 'Fallow', 'Inactive'];
const PLANTING_STATUS = ['Planned', 'Planted', 'Growing', 'Harvested', 'Failed', 'Terminated'];
const CROP_CATEGORY   = ['', 'Grain', 'Forage', 'Vegetable', 'Fruit', 'Oilseed', 'Cover Crop', 'Other'];
const EQUIP_CATEGORY  = ['', 'Tractor', 'Implement', 'Vehicle', 'Irrigation', 'Tool', 'Other'];
const EQUIP_STATUS    = ['Active', 'Maintenance', 'Retired'];
const MAINT_TYPE      = ['', 'Routine', 'Repair', 'Inspection'];
const INV_CATEGORY    = ['', 'Seed', 'Fertilizer', 'Chemical', 'Feed', 'Fuel', 'Supply', 'Harvest', 'Other'];
const TXN_TYPE        = ['Purchase', 'Usage', 'Adjustment'];
const TASK_TYPE       = ['', 'Planting', 'Harvest', 'Irrigation', 'Fertilize', 'Spray', 'Scouting', 'Tillage', 'Maintenance', 'Other'];
const TASK_PRIORITY   = ['Low', 'Medium', 'High'];
const TASK_STATUS     = ['Open', 'In Progress', 'Completed', 'Cancelled'];

const ST_COLOR = {
  Active: B.green, Fallow: B.amber, Inactive: B.textLt, Retired: B.textLt, Maintenance: B.amber,
  Planned: B.navy, Planted: B.tealDk, Growing: B.green, Harvested: B.tealDk, Failed: B.red, Terminated: B.textLt,
  Open: B.amber, 'In Progress': B.navy, Completed: B.green, Cancelled: B.textLt,
  Low: B.tealDk, Medium: B.amber, High: B.red,
};
const SBadge = ({ s }) => s ? <Badge color={ST_COLOR[s] || B.textMd}>{s}</Badge> : <span style={{ color: B.textLt }}>—</span>;

const opts = (arr) => arr.map(v => ({ value: v, label: v || '— Select —' }));

// Generic confirm-then-delete helper.
const confirmDelete = (msg) => window.confirm(msg);

// ===========================================================================
// FarmDetail — tabbed operations view for a single farm
// ===========================================================================
export const FarmDetail = ({ farm, onBack }) => {
  const { toast } = useToast();
  const [tab, setTab] = useState('Overview');
  // mapFocus: hide the summary KPIs when the Land Map is clicked/enlarged.
  const [mapFocus, setMapFocus] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [current, setCurrent] = useState(farm);

  const farmId = current.farmID;
  const { data: summary, reload: reloadSummary } = useApi(() => api.get(`farms/${farmId}/summary`), [farmId]);

  const openEdit = () => {
    setForm({
      name: current.name || '', farmType: current.farmType || '', totalAcres: current.totalAcres ?? '',
      location: current.location || '', status: current.status || 'Active', description: current.description || '',
      centroidLat: current.centroidLat ?? '', centroidLng: current.centroidLng ?? '',
    });
    setEditing(true);
  };

  const saveFarm = async () => {
    try {
      const updated = await api.put(`farms/${farmId}`, form);
      setCurrent({ ...current, ...updated });
      setEditing(false);
      toast.success('Farm updated');
    } catch (e) { toast.error(e.message); }
  };

  const deleteFarm = async () => {
    if (!confirmDelete(`Delete farm "${current.name}"? This also hides its fields, plantings, tasks, and records.`)) return;
    try { await api.del(`farms/${farmId}`); toast.success('Farm deleted'); onBack(); }
    catch (e) { toast.error(e.message); }
  };

  const TABS = ['Overview', 'Weather', 'Fields', 'Plantings', 'Tasks', 'Equipment', 'Inventory', 'Parcels', 'Land Map'];

  return (
    <div>
      {/* Header */}
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: B.textMd, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 12, padding: 0 }}><Icon name="arrowLeft" size={14} /> Back to Farms</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: B.text, margin: 0 }}>{current.name}</h1>
            <SBadge s={current.status} />
          </div>
          <div style={{ fontSize: 13, color: B.textMd, marginTop: 4 }}>
            {[current.dealName, current.farmType, current.location].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={openEdit}>Edit</Btn>
          <Btn variant="danger" onClick={deleteFarm}>Delete</Btn>
        </div>
      </div>

      {/* Summary KPIs */}
      {!mapFocus && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 22 }}>
          <KpiCard label="Fields"          value={summary?.fieldCount ?? '—'}                 sub={summary ? `${num(summary.fieldAcres, 0)} ac` : ''} color={B.navy} />
          <KpiCard label="Active Plantings" value={summary?.activePlantingCount ?? '—'}        color={B.green} />
          <KpiCard label="Open Tasks"      value={summary?.openTaskCount ?? '—'}              color={B.amber} />
          <KpiCard label="Equipment"       value={summary?.equipmentCount ?? '—'}             color={B.tealDk} />
          <KpiCard label="Low Stock"       value={summary?.lowStockCount ?? '—'}              color={summary?.lowStockCount ? B.red : B.textLt} />
        </div>
      )}

      <Tabs tabs={TABS} active={tab} onChange={(t) => { setTab(t); setMapFocus(false); }} />

      {tab === 'Overview'  && <OverviewTab farm={current} />}
      {tab === 'Weather'   && <WeatherTab farm={current} />}
      {tab === 'Fields'    && <FieldsTab farm={current} onChange={reloadSummary} />}
      {tab === 'Plantings' && <PlantingsTab farm={current} onChange={reloadSummary} />}
      {tab === 'Tasks'     && <TasksTab farm={current} onChange={reloadSummary} />}
      {tab === 'Equipment' && <EquipmentTab farm={current} onChange={reloadSummary} />}
      {tab === 'Inventory' && <InventoryTab farm={current} onChange={reloadSummary} />}
      {tab === 'Parcels'   && (
        current.dealID
          ? <ParcelsList dealId={current.dealID} />
          : <Card>No deal linked.</Card>
      )}
      {tab === 'Land Map'  && (
        current.dealID
          ? <ParcelsMap dealId={current.dealID} onExpandChange={setMapFocus} />
          : <Card>No deal linked.</Card>
      )}

      {editing && form && (
        <Modal title="Edit Farm" onClose={() => setEditing(false)}>
          <Input label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Type" value={form.farmType} onChange={v => setForm({ ...form, farmType: v })} options={opts(FARM_TYPES)} />
            <Input label="Total Acres" type="number" value={form.totalAcres} onChange={v => setForm({ ...form, totalAcres: v })} />
          </div>
          <Input label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Latitude" type="number" value={form.centroidLat} onChange={v => setForm({ ...form, centroidLat: v })} placeholder="e.g. 41.8781" />
            <Input label="Longitude" type="number" value={form.centroidLng} onChange={v => setForm({ ...form, centroidLng: v })} placeholder="e.g. -93.0977" />
          </div>
          <div style={{ fontSize: 11, color: B.textLt, marginTop: -8, marginBottom: 8 }}>Optional — pins this farm for weather lookups. If blank, derived from fields/parcels.</div>
          <Select label="Status" value={form.status} onChange={v => setForm({ ...form, status: v })} options={opts(FARM_STATUS)} />
          <Textarea label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn onClick={saveFarm}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const OverviewTab = ({ farm }) => (
  <Card>
    <InfoGrid items={[
      ['Deal', farm.dealName || '—'],
      ['Type', farm.farmType || '—'],
      ['Total Acres', num(farm.totalAcres)],
      ['Location', farm.location || '—'],
      ['Status', farm.status],
      ['Created', fmt.date(farm.createdUTC)],
    ]} cols={3} />
    {farm.description && (
      <>
        <SectionHeader title="Description" />
        <div style={{ fontSize: 13, color: B.text, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{farm.description}</div>
      </>
    )}
  </Card>
);

// ---------------------------------------------------------------------------
// Weather — live conditions + recent rainfall (Open-Meteo), per farm & field
// ---------------------------------------------------------------------------
const inches = (v) => v != null ? `${num(v, 2)} in` : '—';
const tempF = (v) => v != null ? `${num(v, 0)}°F` : '—';

const WeatherTab = ({ farm }) => {
  const { data, loading, error, reload } = useApi(() => api.get(`farms/${farm.farmID}/weather`), [farm.farmID]);

  if (loading) return <Card><Spinner /></Card>;
  if (error)   return <Card><ErrorMsg msg={error} onRetry={reload} /></Card>;

  if (data?.reason === 'no-location' || (!data?.farm && !(data?.fields || []).some(f => f.tempF != null))) {
    return (
      <Card>
        <SectionHeader title="Weather" />
        <div style={{ fontSize: 13, color: B.textMd, lineHeight: 1.6 }}>
          No location is set for this farm yet, so weather can't be looked up. Add coordinates by
          editing the farm (<strong>Edit → Latitude / Longitude</strong>), setting a field's coordinates,
          or linking a field to a GIS parcel under the <strong>Fields</strong> tab.
        </div>
      </Card>
    );
  }

  const w = data.farm;
  const fields = (data.fields || []).filter(f => f.tempF != null || f.rain7d != null);

  const cond = w ? weatherDesc(w.weatherCode) : { label: '—', emoji: '' };

  const fieldCols = [
    { key: 'name', label: 'Field', render: v => <span style={{ fontWeight: 600, color: B.navy }}>{v}</span> },
    { key: 'weatherCode', label: 'Condition', render: v => { const c = weatherDesc(v); return <span>{c.emoji} {c.label}</span>; } },
    { key: 'tempF', label: 'Temp', render: v => tempF(v) },
    { key: 'windMph', label: 'Wind', render: (v, r) => v != null ? `${num(v, 0)} mph ${windCompass(r.windDir)}`.trim() : '—' },
    { key: 'precipNow', label: 'Precip Now', render: v => inches(v) },
    { key: 'rain7d', label: 'Rain 7d', render: v => inches(v) },
    { key: 'rain30d', label: 'Rain 30d', render: v => inches(v) },
  ];

  return (
    <div>
      <SectionHeader title="Weather" action={<Btn size="sm" variant="ghost" onClick={reload}><Icon name="refresh" size={14} /> Refresh</Btn>} />

      {w && (
        <Card style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 30 }}>{cond.emoji}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: B.text }}>{cond.label}</div>
              <div style={{ fontSize: 12, color: B.textLt }}>
                Farm location ({w.source}) · {num(w.lat, 4)}, {num(w.lng, 4)}
                {data.fetchedUTC ? ` · updated ${fmt.date(data.fetchedUTC)}` : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
            <KpiCard label="Temperature"  value={tempF(w.tempF)} color={B.navy} />
            <KpiCard label="Wind"         value={w.windMph != null ? `${num(w.windMph, 0)} mph` : '—'} sub={windCompass(w.windDir)} color={B.tealDk} />
            <KpiCard label="Humidity"     value={w.humidity != null ? `${num(w.humidity, 0)}%` : '—'} color={B.tealDk} />
            <KpiCard label="Precip Now"   value={inches(w.precipNow)} color={B.navy} />
            <KpiCard label="Rain — 7 day"  value={inches(w.rain7d)}  color={B.green} />
            <KpiCard label="Rain — 30 day" value={inches(w.rain30d)} color={B.green} />
          </div>
        </Card>
      )}

      {fields.length > 0 && (
        <>
          <SectionHeader title="By Field" />
          <Card style={{ padding: 0 }}><Table columns={fieldCols} data={fields} /></Card>
        </>
      )}

      <div style={{ fontSize: 11, color: B.textLt, marginTop: 12 }}>Weather data from Open-Meteo. Rain totals are accumulated precipitation.</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------
const FieldsTab = ({ farm, onChange }) => {
  const { toast } = useToast();
  const farmId = farm.farmID;
  const { data: fields, loading, error, reload } = useApi(() => api.get(`fields?farmId=${farmId}`), [farmId]);
  // Parcel geometry — shown as context outlines under the boundary editor.
  const { data: parcelMapFC } = useApi(() => farm.dealID ? api.get(`parcel-map?dealId=${farm.dealID}`) : Promise.resolve(null), [farm.dealID]);
  const [modal, setModal] = useState(null);   // null | { ...form, fieldID?, geometry? }
  const [detecting, setDetecting] = useState(false); // AI field-detection modal open?
  const [showBoundary, setShowBoundary] = useState(false); // boundary editor visible in the field modal?

  const parseGeom = (g) => { try { return g ? JSON.parse(g) : null; } catch { return null; } };
  // A field is defined by its boundary: acres + centroid derive from it, so
  // there's no manual lat/long or parcel picker (a field can span parcels, and
  // a parcel can hold many fields — a single link would be wrong).
  const blank = { name: '', acres: '', soilType: '', landUse: '', irrigationType: '', status: 'Active', notes: '', centroidLat: '', centroidLng: '', geometry: null };
  const openNew  = () => { setShowBoundary(false); setModal({ ...blank }); };
  const openEdit = (f) => { setShowBoundary(false); setModal({
    fieldID: f.fieldID, name: f.name || '', acres: f.acres ?? '', soilType: f.soilType || '',
    landUse: f.landUse || '', irrigationType: f.irrigationType || '', status: f.status || 'Active',
    notes: f.notes || '', centroidLat: f.centroidLat ?? '', centroidLng: f.centroidLng ?? '',
    geometry: parseGeom(f.geometryJson),
  }); };

  // Draw a brand-new field by hand: open the modal with the boundary editor up.
  const openDraw = () => { setModal({ ...blank }); setShowBoundary(true); };

  // Reshape/draw handler for the single-boundary editor in the field modal —
  // acres + centroid recompute from the drawn boundary.
  const onBoundaryChange = (feats) => {
    const geometry = feats.length ? feats[feats.length - 1].geometry : null;
    setModal(m => {
      if (!m) return m;
      const centroid = geometry ? polygonCentroid(geometry) : null;
      const acres = geometry ? Math.round(polygonAcres(geometry) * 100) / 100 : '';
      return {
        ...m,
        geometry,
        acres: geometry ? String(acres) : m.acres,
        centroidLat: centroid ? centroid.lat : m.centroidLat,
        centroidLng: centroid ? centroid.lng : m.centroidLng,
      };
    });
  };

  const save = async () => {
    if (!modal.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (modal.fieldID) await api.put(`fields/${modal.fieldID}`, { ...modal, clearGeometry: !modal.geometry });
      else               await api.post('fields', { ...modal, farmId });
      toast.success('Field saved'); setModal(null); setShowBoundary(false); reload(); onChange?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (f) => {
    if (!confirmDelete(`Delete field "${f.name}"?`)) return;
    try { await api.del(`fields/${f.fieldID}`); toast.success('Field deleted'); reload(); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };

  const columns = [
    { key: 'name', label: 'Field', render: (v, r) => <span style={{ fontWeight: 600, color: B.navy }}>{v}</span> },
    { key: 'acres', label: 'Acres', render: (v, r) => num(v ?? r.regridAcres) },
    { key: 'landUse', label: 'Land Use', render: v => v || '—' },
    { key: 'soilType', label: 'Soil', render: v => v || '—' },
    { key: 'irrigationType', label: 'Irrigation', render: v => v || '—' },
    { key: 'currentCrop', label: 'Current Crop', render: v => v ? <Badge color={B.green}>{v}</Badge> : '—' },
    { key: 'status', label: 'Status', render: v => <SBadge s={v} /> },
    { key: '_act', label: '', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
        <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Fields" action={
        <div style={{ display: 'flex', gap: 8 }}>
          {farm.dealID && <Btn size="sm" variant="ghost" onClick={() => setDetecting(true)}>🛰️ Detect from imagery</Btn>}
          {farm.dealID && <Btn size="sm" variant="ghost" onClick={openDraw}>✏️ Draw field</Btn>}
          <Btn size="sm" onClick={openNew}>+ Add Field</Btn>
        </div>
      } />
      <Card style={{ padding: 0 }}>
        {loading ? <Spinner /> : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : <Table columns={columns} data={fields} />}
      </Card>

      {modal && (
        <Modal
          title={modal.fieldID ? 'Edit Field' : 'Add Field'}
          width={showBoundary ? 720 : 560}
          onClose={() => { setModal(null); setShowBoundary(false); }}
        >
          <Input label="Name" value={modal.name} onChange={v => setModal({ ...modal, name: v })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Acres" type="number" value={modal.acres} onChange={v => setModal({ ...modal, acres: v })} />
            <Select label="Status" value={modal.status} onChange={v => setModal({ ...modal, status: v })} options={opts(FIELD_STATUS)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Land Use" value={modal.landUse} onChange={v => setModal({ ...modal, landUse: v })} options={opts(LAND_USE)} />
            <Select label="Irrigation" value={modal.irrigationType} onChange={v => setModal({ ...modal, irrigationType: v })} options={opts(IRRIGATION)} />
          </div>
          <Input label="Soil Type" value={modal.soilType} onChange={v => setModal({ ...modal, soilType: v })} />

          {/* Field boundary — draw a new one or reshape the existing one on a map.
              Acres + location come from the boundary; there's no manual lat/long
              or parcel link (a field can span parcels). */}
          {farm.dealID && (
            <div style={{ margin: '4px 0 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" onClick={() => setShowBoundary(s => !s)}
                  style={{ background: 'none', border: 'none', color: B.navy, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, padding: 0 }}>
                  {showBoundary ? '▾' : '▸'} {modal.geometry ? 'Edit boundary on map' : 'Draw boundary on map'}
                </button>
                {modal.geometry && !showBoundary && (
                  <span style={{ fontSize: 11, color: B.textLt }}>Boundary set{modal.acres ? ` · ~${modal.acres} ac` : ''}</span>
                )}
                {modal.geometry && (
                  <button type="button" onClick={() => setModal({ ...modal, geometry: null })}
                    style={{ background: 'none', border: 'none', color: B.red, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, padding: 0, marginLeft: 'auto' }}>
                    Clear boundary
                  </button>
                )}
              </div>
              {showBoundary && (
                <div style={{ marginTop: 8 }}>
                  <FieldBoundaryEditor
                    key={modal.fieldID || 'new'}
                    parcelsFC={parcelMapFC}
                    initialFeatures={modal.geometry ? [{ id: 'f', geometry: modal.geometry }] : []}
                    onFeaturesChange={onBoundaryChange}
                    single
                    height={320}
                  />
                  <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>
                    Polygon tool (top-right) to draw · drag points to reshape · trash to clear. Acres & centroid fill in automatically.
                  </div>
                </div>
              )}
            </div>
          )}

          <Textarea label="Notes" value={modal.notes} onChange={v => setModal({ ...modal, notes: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => { setModal(null); setShowBoundary(false); }}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}

      {detecting && (
        <FieldDetectionModal
          farm={farm}
          onClose={() => setDetecting(false)}
          onCreated={({ created, failed } = {}) => {
            if (created) toast.success(`Created ${created} field${created === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
            else if (failed) toast.error(`Could not create ${failed} field${failed === 1 ? '' : 's'}`);
            reload(); onChange?.();
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Plantings (+ inline crop quick-add + harvest logging)
// ---------------------------------------------------------------------------
const PlantingsTab = ({ farm, onChange }) => {
  const { toast } = useToast();
  const farmId = farm.farmID;
  const { data: plantings, loading, error, reload } = useApi(() => api.get(`plantings?farmId=${farmId}`), [farmId]);
  const { data: fields }   = useApi(() => api.get(`fields?farmId=${farmId}`), [farmId]);
  const { data: crops, reload: reloadCrops } = useApi(() => api.get('crops'), []);
  const [modal, setModal]     = useState(null);
  const [harvestFor, setHarvestFor] = useState(null);

  const blank = { fieldId: '', cropId: '', season: '', seasonYear: new Date().getFullYear(), plannedDate: '', plantedDate: '', expectedHarvestDate: '', acresPlanted: '', seedingRate: '', expectedYield: '', yieldUnit: '', status: 'Planned', notes: '' };
  const openNew  = () => setModal({ ...blank });
  const openEdit = (p) => setModal({
    plantingID: p.plantingID, fieldId: p.fieldID || '', cropId: p.cropID || '', season: p.season || '',
    seasonYear: p.seasonYear ?? '', plannedDate: p.plannedDate || '', plantedDate: p.plantedDate || '',
    expectedHarvestDate: p.expectedHarvestDate || '', acresPlanted: p.acresPlanted ?? '', seedingRate: p.seedingRate ?? '',
    expectedYield: p.expectedYield ?? '', yieldUnit: p.yieldUnit || '', status: p.status || 'Planned', notes: p.notes || '',
  });

  const save = async () => {
    if (!modal.fieldId) { toast.error('Field is required'); return; }
    if (!modal.cropId)  { toast.error('Crop is required'); return; }
    try {
      if (modal.plantingID) await api.put(`plantings/${modal.plantingID}`, modal);
      else                  await api.post('plantings', modal);
      toast.success('Planting saved'); setModal(null); reload(); onChange?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (p) => {
    if (!confirmDelete(`Delete this ${p.cropName} planting?`)) return;
    try { await api.del(`plantings/${p.plantingID}`); toast.success('Planting deleted'); reload(); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };

  const columns = [
    { key: 'cropName', label: 'Crop', render: (v, r) => (
      <div><div style={{ fontWeight: 600, color: B.navy }}>{v}</div><div style={{ fontSize: 11, color: B.textLt }}>{r.fieldName}</div></div>
    )},
    { key: 'season', label: 'Season', render: (v, r) => v || r.seasonYear || '—' },
    { key: 'acresPlanted', label: 'Acres', render: v => num(v) },
    { key: 'plantedDate', label: 'Planted', render: v => fmt.date(v) },
    { key: 'expectedHarvestDate', label: 'Exp. Harvest', render: v => fmt.date(v) },
    { key: 'harvestedQuantity', label: 'Harvested', render: (v, r) => v ? `${num(v)} ${r.defaultUnit || r.yieldUnit || ''}`.trim() : '—' },
    { key: 'status', label: 'Status', render: v => <SBadge s={v} /> },
    { key: '_act', label: '', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Btn size="sm" variant="ghost" onClick={() => setHarvestFor(r)}>Harvest</Btn>
        <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
        <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Plantings" action={<Btn size="sm" onClick={openNew}>+ Add Planting</Btn>} />
      <Card style={{ padding: 0 }}>
        {loading ? <Spinner /> : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : <Table columns={columns} data={plantings} />}
      </Card>

      {modal && (
        <Modal title={modal.plantingID ? 'Edit Planting' : 'Add Planting'} onClose={() => setModal(null)} width={640}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Field *" value={modal.fieldId} onChange={v => setModal({ ...modal, fieldId: v })}
              options={[{ value: '', label: '— Select field —' }, ...((fields || []).map(f => ({ value: f.fieldID, label: f.name })))]} />
            <CropSelect crops={crops} value={modal.cropId} onChange={v => setModal({ ...modal, cropId: v })} onCreated={reloadCrops} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="Season" value={modal.season} onChange={v => setModal({ ...modal, season: v })} placeholder="e.g. Spring" />
            <Input label="Season Year" type="number" value={modal.seasonYear} onChange={v => setModal({ ...modal, seasonYear: v })} />
            <Input label="Acres Planted" type="number" value={modal.acresPlanted} onChange={v => setModal({ ...modal, acresPlanted: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="Planned" type="date" value={modal.plannedDate} onChange={v => setModal({ ...modal, plannedDate: v })} />
            <Input label="Planted" type="date" value={modal.plantedDate} onChange={v => setModal({ ...modal, plantedDate: v })} />
            <Input label="Exp. Harvest" type="date" value={modal.expectedHarvestDate} onChange={v => setModal({ ...modal, expectedHarvestDate: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="Seeding Rate" type="number" value={modal.seedingRate} onChange={v => setModal({ ...modal, seedingRate: v })} />
            <Input label="Expected Yield" type="number" value={modal.expectedYield} onChange={v => setModal({ ...modal, expectedYield: v })} />
            <Input label="Yield Unit" value={modal.yieldUnit} onChange={v => setModal({ ...modal, yieldUnit: v })} placeholder="bu, tons…" />
          </div>
          <Select label="Status" value={modal.status} onChange={v => setModal({ ...modal, status: v })} options={opts(PLANTING_STATUS)} />
          <Textarea label="Notes" value={modal.notes} onChange={v => setModal({ ...modal, notes: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}

      {harvestFor && <HarvestModal planting={harvestFor} onClose={() => setHarvestFor(null)} onSaved={() => { reload(); onChange?.(); }} />}
    </div>
  );
};

// Crop dropdown with inline "new crop" creation.
const CropSelect = ({ crops, value, onChange, onCreated }) => {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', category: '', defaultUnit: '' });

  const create = async () => {
    if (!draft.name.trim()) { toast.error('Crop name is required'); return; }
    try {
      const c = await api.post('crops', draft);
      toast.success('Crop added'); setAdding(false); setDraft({ name: '', category: '', defaultUnit: '' });
      onCreated?.(); onChange(c.cropID);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: B.textMd, marginBottom: 4 }}>Crop *</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select value={value ?? ''} onChange={e => onChange(e.target.value)}
          style={{ flex: 1, padding: '9px 12px', border: `1px solid ${B.border}`, borderRadius: 6, fontSize: 13, color: B.text, background: B.white, fontFamily: 'inherit' }}>
          <option value="">— Select crop —</option>
          {(crops || []).map(c => <option key={c.cropID} value={c.cropID}>{c.name}{c.variety ? ` (${c.variety})` : ''}</option>)}
        </select>
        <Btn size="sm" variant="ghost" aria-label={adding ? 'Cancel' : 'Add'} onClick={() => setAdding(a => !a)}><Icon name={adding ? 'close' : 'add'} size={14} /></Btn>
      </div>
      {adding && (
        <div style={{ marginTop: 8, padding: 12, background: B.surfaceMuted, borderRadius: 6 }}>
          <Input label="New Crop Name" value={draft.name} onChange={v => setDraft({ ...draft, name: v })} autoFocus />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Select label="Category" value={draft.category} onChange={v => setDraft({ ...draft, category: v })} options={opts(CROP_CATEGORY)} />
            <Input label="Default Unit" value={draft.defaultUnit} onChange={v => setDraft({ ...draft, defaultUnit: v })} placeholder="bu, tons" />
          </div>
          <Btn size="sm" onClick={create}>Add Crop</Btn>
        </div>
      )}
    </div>
  );
};

// Harvest log modal for a planting.
const HarvestModal = ({ planting, onClose, onSaved }) => {
  const { toast } = useToast();
  const { data: harvests, loading, reload } = useApi(() => api.get(`plantings/${planting.plantingID}/harvests`), [planting.plantingID]);
  const [form, setForm] = useState({ harvestDate: '', quantity: '', unit: planting.defaultUnit || planting.yieldUnit || '', acresHarvested: '', quality: '', notes: '' });

  const save = async () => {
    try {
      await api.post(`plantings/${planting.plantingID}/harvests`, form);
      toast.success('Harvest logged');
      setForm({ harvestDate: '', quantity: '', unit: form.unit, acresHarvested: '', quality: '', notes: '' });
      reload(); onSaved?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (h) => {
    if (!confirmDelete('Delete this harvest record?')) return;
    try { await api.del(`harvests/${h.harvestID}`); toast.success('Deleted'); reload(); onSaved?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Modal title={`Harvests — ${planting.cropName}`} onClose={onClose} width={620}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Input label="Date" type="date" value={form.harvestDate} onChange={v => setForm({ ...form, harvestDate: v })} />
        <Input label="Quantity" type="number" value={form.quantity} onChange={v => setForm({ ...form, quantity: v })} />
        <Input label="Unit" value={form.unit} onChange={v => setForm({ ...form, unit: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Input label="Acres Harvested" type="number" value={form.acresHarvested} onChange={v => setForm({ ...form, acresHarvested: v })} />
        <Input label="Quality" value={form.quality} onChange={v => setForm({ ...form, quality: v })} />
      </div>
      <Input label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={save}>+ Log Harvest</Btn></div>

      {loading ? <Spinner /> : (
        <Table columns={[
          { key: 'harvestDate', label: 'Date', render: v => fmt.date(v) },
          { key: 'quantity', label: 'Qty', render: (v, r) => `${num(v)} ${r.unit || ''}`.trim() },
          { key: 'acresHarvested', label: 'Acres', render: v => num(v) },
          { key: 'yieldPerAcre', label: 'Yield/Ac', render: v => num(v) },
          { key: 'quality', label: 'Quality', render: v => v || '—' },
          { key: '_act', label: '', render: (_, r) => <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn> },
        ]} data={harvests} />
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------
const TasksTab = ({ farm, onChange }) => {
  const { toast } = useToast();
  const farmId = farm.farmID;
  const [statusFilter, setStatusFilter] = useState('');
  const { data: tasks, loading, error, reload } = useApi(
    () => api.get(`farm-tasks?farmId=${farmId}${statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : ''}`),
    [farmId, statusFilter]
  );
  const { data: fields }    = useApi(() => api.get(`fields?farmId=${farmId}`), [farmId]);
  const { data: equipment } = useApi(() => api.get(`equipment?farmId=${farmId}`), [farmId]);
  const { data: users }     = useApi(() => api.get('users'), []);
  const [modal, setModal] = useState(null);

  const blank = { title: '', description: '', taskType: '', priority: 'Medium', status: 'Open', dueDate: '', fieldId: '', equipmentId: '', assignedToUserId: '' };
  const openNew  = () => setModal({ ...blank });
  const openEdit = (t) => setModal({
    farmTaskID: t.farmTaskID, title: t.title || '', description: t.description || '', taskType: t.taskType || '',
    priority: t.priority || 'Medium', status: t.status || 'Open', dueDate: t.dueDate || '',
    fieldId: t.fieldID || '', equipmentId: t.equipmentID || '', assignedToUserId: t.assignedToUserID || '',
  });

  const save = async () => {
    if (!modal.title.trim()) { toast.error('Title is required'); return; }
    try {
      if (modal.farmTaskID) await api.put(`farm-tasks/${modal.farmTaskID}`, modal);
      else                  await api.post('farm-tasks', { ...modal, farmId });
      toast.success('Task saved'); setModal(null); reload(); onChange?.();
    } catch (e) { toast.error(e.message); }
  };
  const complete = async (t) => {
    try { await api.put(`farm-tasks/${t.farmTaskID}`, { ...t, fieldId: t.fieldID, equipmentId: t.equipmentID, assignedToUserId: t.assignedToUserID, status: 'Completed' }); reload(); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };
  const del = async (t) => {
    if (!confirmDelete(`Delete task "${t.title}"?`)) return;
    try { await api.del(`farm-tasks/${t.farmTaskID}`); toast.success('Deleted'); reload(); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };

  const columns = [
    { key: 'title', label: 'Task', render: (v, r) => (
      <div><div style={{ fontWeight: 600, color: B.navy }}>{v}</div>
      <div style={{ fontSize: 11, color: B.textLt }}>{[r.taskType, r.fieldName, r.cropName, r.equipmentName].filter(Boolean).join(' · ') || '—'}</div></div>
    )},
    { key: 'priority', label: 'Priority', render: v => <SBadge s={v} /> },
    { key: 'dueDate', label: 'Due', render: v => fmt.date(v) },
    { key: 'assignedToName', label: 'Assignee', render: v => v || '—' },
    { key: 'status', label: 'Status', render: v => <SBadge s={v} /> },
    { key: '_act', label: '', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        {r.status !== 'Completed' && r.status !== 'Cancelled' && <Btn size="sm" variant="ghost" aria-label="Complete" onClick={() => complete(r)}><Icon name="check" size={14} /></Btn>}
        <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
        <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Tasks" action={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', border: `1px solid ${B.border}`, borderRadius: 6, fontSize: 12, fontFamily: 'inherit', color: B.text }}>
            <option value="">All statuses</option>
            {TASK_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Btn size="sm" onClick={openNew}>+ Add Task</Btn>
        </div>
      } />
      <Card style={{ padding: 0 }}>
        {loading ? <Spinner /> : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : <Table columns={columns} data={tasks} />}
      </Card>

      {modal && (
        <Modal title={modal.farmTaskID ? 'Edit Task' : 'Add Task'} onClose={() => setModal(null)} width={620}>
          <Input label="Title" value={modal.title} onChange={v => setModal({ ...modal, title: v })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Select label="Type" value={modal.taskType} onChange={v => setModal({ ...modal, taskType: v })} options={opts(TASK_TYPE)} />
            <Select label="Priority" value={modal.priority} onChange={v => setModal({ ...modal, priority: v })} options={opts(TASK_PRIORITY)} />
            <Input label="Due Date" type="date" value={modal.dueDate} onChange={v => setModal({ ...modal, dueDate: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Field" value={modal.fieldId} onChange={v => setModal({ ...modal, fieldId: v })}
              options={[{ value: '', label: '— None —' }, ...((fields || []).map(f => ({ value: f.fieldID, label: f.name })))]} />
            <Select label="Equipment" value={modal.equipmentId} onChange={v => setModal({ ...modal, equipmentId: v })}
              options={[{ value: '', label: '— None —' }, ...((equipment || []).map(e => ({ value: e.equipmentID, label: e.name })))]} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Assignee" value={modal.assignedToUserId} onChange={v => setModal({ ...modal, assignedToUserId: v })}
              options={[{ value: '', label: '— Unassigned —' }, ...((users || []).map(u => ({ value: u.userID, label: u.displayName })))]} />
            <Select label="Status" value={modal.status} onChange={v => setModal({ ...modal, status: v })} options={opts(TASK_STATUS)} />
          </div>
          <Textarea label="Description" value={modal.description} onChange={v => setModal({ ...modal, description: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Equipment (+ maintenance log)
// ---------------------------------------------------------------------------
const EquipmentTab = ({ farm, onChange }) => {
  const { toast } = useToast();
  const farmId = farm.farmID;
  const { data: equipment, loading, error, reload } = useApi(() => api.get(`equipment?farmId=${farmId}`), [farmId]);
  const [modal, setModal] = useState(null);
  const [maintFor, setMaintFor] = useState(null);

  const blank = { name: '', category: '', make: '', model: '', modelYear: '', serialNo: '', status: 'Active', purchaseDate: '', purchasePrice: '', hoursMeter: '', milesMeter: '', location: '', notes: '' };
  const openNew  = () => setModal({ ...blank });
  const openEdit = (e) => setModal({
    equipmentID: e.equipmentID, name: e.name || '', category: e.category || '', make: e.make || '', model: e.model || '',
    modelYear: e.modelYear ?? '', serialNo: e.serialNo || '', status: e.status || 'Active', purchaseDate: e.purchaseDate || '',
    purchasePrice: e.purchasePrice ?? '', hoursMeter: e.hoursMeter ?? '', milesMeter: e.milesMeter ?? '', location: e.location || '', notes: e.notes || '',
  });

  const save = async () => {
    if (!modal.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (modal.equipmentID) await api.put(`equipment/${modal.equipmentID}`, modal);
      else                   await api.post('equipment', { ...modal, farmId });
      toast.success('Equipment saved'); setModal(null); reload(); onChange?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (e) => {
    if (!confirmDelete(`Delete "${e.name}"?`)) return;
    try { await api.del(`equipment/${e.equipmentID}`); toast.success('Deleted'); reload(); onChange?.(); }
    catch (e2) { toast.error(e2.message); }
  };

  const columns = [
    { key: 'name', label: 'Equipment', render: (v, r) => (
      <div><div style={{ fontWeight: 600, color: B.navy }}>{v}</div>
      <div style={{ fontSize: 11, color: B.textLt }}>{[r.make, r.model, r.modelYear].filter(Boolean).join(' ') || r.category || '—'}</div></div>
    )},
    { key: 'category', label: 'Category', render: v => v || '—' },
    { key: 'usage', label: 'Hours / Miles', render: (_, r) => {
      const hasHours = r.hoursMeter != null && r.hoursMeter !== '';
      const hasMiles = r.milesMeter != null && r.milesMeter !== '';
      const parts = [];
      if (hasHours) parts.push(`${num(r.hoursMeter)} h`);
      if (hasMiles) parts.push(`${num(r.milesMeter)} m`);
      return parts.length ? parts.join(' · ') : '—';
    } },
    { key: 'lastServiceDate', label: 'Last Service', render: v => fmt.date(v) },
    { key: 'nextServiceDate', label: 'Next Service', render: v => fmt.date(v) },
    { key: 'status', label: 'Status', render: v => <SBadge s={v} /> },
    { key: '_act', label: '', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Btn size="sm" variant="ghost" onClick={() => setMaintFor(r)}>Service</Btn>
        <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
        <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Equipment" action={<Btn size="sm" onClick={openNew}>+ Add Equipment</Btn>} />
      <Card style={{ padding: 0 }}>
        {loading ? <Spinner /> : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : <Table columns={columns} data={equipment} />}
      </Card>

      {modal && (
        <Modal title={modal.equipmentID ? 'Edit Equipment' : 'Add Equipment'} onClose={() => setModal(null)} width={640}>
          <Input label="Name" value={modal.name} onChange={v => setModal({ ...modal, name: v })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Category" value={modal.category} onChange={v => setModal({ ...modal, category: v })} options={opts(EQUIP_CATEGORY)} />
            <Select label="Status" value={modal.status} onChange={v => setModal({ ...modal, status: v })} options={opts(EQUIP_STATUS)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="Make" value={modal.make} onChange={v => setModal({ ...modal, make: v })} />
            <Input label="Model" value={modal.model} onChange={v => setModal({ ...modal, model: v })} />
            <Input label="Year" type="number" value={modal.modelYear} onChange={v => setModal({ ...modal, modelYear: v })} />
          </div>
          <Input label="Serial No." value={modal.serialNo} onChange={v => setModal({ ...modal, serialNo: v })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Hours Meter" type="number" value={modal.hoursMeter} onChange={v => setModal({ ...modal, hoursMeter: v })} />
            <Input label="Miles (Odometer)" type="number" value={modal.milesMeter} onChange={v => setModal({ ...modal, milesMeter: v })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Purchase Date" type="date" value={modal.purchaseDate} onChange={v => setModal({ ...modal, purchaseDate: v })} />
            <Input label="Purchase Price" type="number" value={modal.purchasePrice} onChange={v => setModal({ ...modal, purchasePrice: v })} />
          </div>
          <Input label="Location" value={modal.location} onChange={v => setModal({ ...modal, location: v })} />
          <Textarea label="Notes" value={modal.notes} onChange={v => setModal({ ...modal, notes: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}

      {maintFor && <MaintenanceModal equipment={maintFor} onClose={() => setMaintFor(null)} onSaved={() => { reload(); onChange?.(); }} />}
    </div>
  );
};

const MaintenanceModal = ({ equipment, onClose, onSaved }) => {
  const { toast } = useToast();
  const { data: logs, loading, reload } = useApi(() => api.get(`equipment/${equipment.equipmentID}/maintenance`), [equipment.equipmentID]);
  const [form, setForm] = useState({ maintenanceDate: '', maintenanceType: '', description: '', cost: '', performedBy: '', nextServiceDate: '' });

  const save = async () => {
    try {
      await api.post(`equipment/${equipment.equipmentID}/maintenance`, form);
      toast.success('Maintenance logged');
      setForm({ maintenanceDate: '', maintenanceType: '', description: '', cost: '', performedBy: '', nextServiceDate: '' });
      reload(); onSaved?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (m) => {
    if (!confirmDelete('Delete this maintenance record?')) return;
    try { await api.del(`maintenance/${m.maintenanceID}`); toast.success('Deleted'); reload(); onSaved?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Modal title={`Maintenance — ${equipment.name}`} onClose={onClose} width={640}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Input label="Date" type="date" value={form.maintenanceDate} onChange={v => setForm({ ...form, maintenanceDate: v })} />
        <Select label="Type" value={form.maintenanceType} onChange={v => setForm({ ...form, maintenanceType: v })} options={opts(MAINT_TYPE)} />
        <Input label="Cost" type="number" value={form.cost} onChange={v => setForm({ ...form, cost: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Input label="Performed By" value={form.performedBy} onChange={v => setForm({ ...form, performedBy: v })} />
        <Input label="Next Service" type="date" value={form.nextServiceDate} onChange={v => setForm({ ...form, nextServiceDate: v })} />
      </div>
      <Input label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={save}>+ Log Service</Btn></div>

      {loading ? <Spinner /> : (
        <Table columns={[
          { key: 'maintenanceDate', label: 'Date', render: v => fmt.date(v) },
          { key: 'maintenanceType', label: 'Type', render: v => v || '—' },
          { key: 'description', label: 'Description', render: v => v || '—' },
          { key: 'cost', label: 'Cost', render: v => v != null ? fmt.currency(v) : '—' },
          { key: 'nextServiceDate', label: 'Next', render: v => fmt.date(v) },
          { key: '_act', label: '', render: (_, r) => <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn> },
        ]} data={logs} />
      )}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Inventory (+ stock transactions)
// ---------------------------------------------------------------------------
const InventoryTab = ({ farm, onChange }) => {
  const { toast } = useToast();
  const farmId = farm.farmID;
  const { data: items, loading, error, reload } = useApi(() => api.get(`inventory?farmId=${farmId}`), [farmId]);
  const [modal, setModal] = useState(null);
  const [txnFor, setTxnFor] = useState(null);

  const blank = { name: '', category: '', unit: '', quantityOnHand: '', reorderLevel: '', unitCost: '', location: '', notes: '' };
  const openNew  = () => setModal({ ...blank });
  const openEdit = (i) => setModal({
    inventoryItemID: i.inventoryItemID, name: i.name || '', category: i.category || '', unit: i.unit || '',
    reorderLevel: i.reorderLevel ?? '', unitCost: i.unitCost ?? '', location: i.location || '', notes: i.notes || '',
  });

  const save = async () => {
    if (!modal.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (modal.inventoryItemID) await api.put(`inventory/${modal.inventoryItemID}`, modal);
      else                       await api.post('inventory', { ...modal, farmId });
      toast.success('Item saved'); setModal(null); reload(); onChange?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (i) => {
    if (!confirmDelete(`Delete "${i.name}"?`)) return;
    try { await api.del(`inventory/${i.inventoryItemID}`); toast.success('Deleted'); reload(); onChange?.(); }
    catch (e) { toast.error(e.message); }
  };

  const columns = [
    { key: 'name', label: 'Item', render: (v, r) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600, color: B.navy }}>{v}</span>
        {r.lowStock && <Badge color={B.red}>Low</Badge>}
      </div>
    )},
    { key: 'category', label: 'Category', render: v => v || '—' },
    { key: 'quantityOnHand', label: 'On Hand', render: (v, r) => `${num(v)} ${r.unit || ''}`.trim() },
    { key: 'reorderLevel', label: 'Reorder At', render: v => num(v) },
    { key: 'unitCost', label: 'Unit Cost', render: v => v != null ? fmt.currency(v) : '—' },
    { key: 'location', label: 'Location', render: v => v || '—' },
    { key: '_act', label: '', render: (_, r) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Btn size="sm" variant="ghost" onClick={() => setTxnFor(r)}>Stock</Btn>
        <Btn size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Btn>
        <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn>
      </div>
    )},
  ];

  return (
    <div>
      <SectionHeader title="Inventory" action={<Btn size="sm" onClick={openNew}>+ Add Item</Btn>} />
      <Card style={{ padding: 0 }}>
        {loading ? <Spinner /> : error ? <div style={{ padding: 24 }}><ErrorMsg msg={error} onRetry={reload} /></div>
          : <Table columns={columns} data={items} />}
      </Card>

      {modal && (
        <Modal title={modal.inventoryItemID ? 'Edit Item' : 'Add Inventory Item'} onClose={() => setModal(null)}>
          <Input label="Name" value={modal.name} onChange={v => setModal({ ...modal, name: v })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Category" value={modal.category} onChange={v => setModal({ ...modal, category: v })} options={opts(INV_CATEGORY)} />
            <Input label="Unit" value={modal.unit} onChange={v => setModal({ ...modal, unit: v })} placeholder="lbs, gal, bags…" />
          </div>
          {!modal.inventoryItemID && (
            <Input label="Starting Quantity" type="number" value={modal.quantityOnHand} onChange={v => setModal({ ...modal, quantityOnHand: v })} />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Reorder Level" type="number" value={modal.reorderLevel} onChange={v => setModal({ ...modal, reorderLevel: v })} />
            <Input label="Unit Cost" type="number" value={modal.unitCost} onChange={v => setModal({ ...modal, unitCost: v })} />
          </div>
          <Input label="Location" value={modal.location} onChange={v => setModal({ ...modal, location: v })} />
          <Textarea label="Notes" value={modal.notes} onChange={v => setModal({ ...modal, notes: v })} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            <Btn onClick={save}>Save</Btn>
          </div>
        </Modal>
      )}

      {txnFor && <TransactionModal item={txnFor} onClose={() => setTxnFor(null)} onSaved={() => { reload(); onChange?.(); }} />}
    </div>
  );
};

const TransactionModal = ({ item, onClose, onSaved }) => {
  const { toast } = useToast();
  const { data: txns, loading, reload } = useApi(() => api.get(`inventory/${item.inventoryItemID}/transactions`), [item.inventoryItemID]);
  const [form, setForm] = useState({ transactionType: 'Purchase', quantity: '', transactionDate: '', unitCost: item.unitCost ?? '', notes: '' });

  const save = async () => {
    if (!form.quantity) { toast.error('Quantity is required'); return; }
    try {
      await api.post(`inventory/${item.inventoryItemID}/transactions`, form);
      toast.success('Stock updated');
      setForm({ transactionType: form.transactionType, quantity: '', transactionDate: '', unitCost: item.unitCost ?? '', notes: '' });
      reload(); onSaved?.();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (t) => {
    if (!confirmDelete('Delete this movement? Stock balance will be reversed.')) return;
    try { await api.del(`inventory-transactions/${t.transactionID}`); toast.success('Deleted'); reload(); onSaved?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Modal title={`Stock — ${item.name}`} onClose={onClose} width={640}>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>On hand: <strong style={{ color: B.text }}>{num(item.quantityOnHand)} {item.unit || ''}</strong></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Select label="Type" value={form.transactionType} onChange={v => setForm({ ...form, transactionType: v })} options={opts(TXN_TYPE)} />
        <Input label="Quantity" type="number" value={form.quantity} onChange={v => setForm({ ...form, quantity: v })} />
        <Input label="Date" type="date" value={form.transactionDate} onChange={v => setForm({ ...form, transactionDate: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <Input label="Unit Cost" type="number" value={form.unitCost} onChange={v => setForm({ ...form, unitCost: v })} />
        <Input label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
      </div>
      <div style={{ fontSize: 11, color: B.textLt, marginBottom: 8 }}>Purchase adds stock · Usage consumes stock · Adjustment uses the entered sign.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}><Btn size="sm" onClick={save}>+ Post Movement</Btn></div>

      {loading ? <Spinner /> : (
        <Table columns={[
          { key: 'transactionDate', label: 'Date', render: v => fmt.date(v) },
          { key: 'transactionType', label: 'Type', render: v => <Badge color={v === 'Purchase' ? B.green : v === 'Usage' ? B.red : B.amber}>{v}</Badge> },
          { key: 'quantity', label: 'Qty', render: v => <span style={{ color: v < 0 ? B.red : B.green }}>{v > 0 ? '+' : ''}{num(v)}</span> },
          { key: 'fieldName', label: 'Field', render: v => v || '—' },
          { key: 'notes', label: 'Notes', render: v => v || '—' },
          { key: '_act', label: '', render: (_, r) => <Btn size="sm" variant="ghost" aria-label="Delete" onClick={() => del(r)}><Icon name="trash" size={14} /></Btn> },
        ]} data={txns} />
      )}
    </Modal>
  );
};
